import { State } from '@state/model';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { Store } from '@ngrx/store';
import { selectPaymentMethods } from '@payment/state/payment.selectors';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';
import { PaymentMethod } from '@egovsolutions/angular-payment-methods';

@Component({
    selector: 'app-payment-method-selection-list',
    template: `
        <ol class="methods-list">
            <li *ngIf="allowNoneOption && selectedPaymentMethod">
                <button mat-button class="clear" (click)="selectMethod(null)">
                    <div class="payment-method-item-content">
                        <div class="identifying-info">
                            <div class="label">None</div>
                            <mat-icon>highlight_off</mat-icon>
                        </div>
                    </div>
                </button>
            </li>
            <li *ngFor="let paymentMethod of methods$ | async">
                <app-payment-method-list-item
                    [paymentMethod]="paymentMethod"
                    [selected]="paymentMethod.id === selectedPaymentMethod"
                    (itemSelected)="selectMethod(paymentMethod.id)"
                ></app-payment-method-list-item>
            </li>
            <li *ngIf="allowCreation">
                <button mat-button class="clear" (click)="newMethodRequested.emit()">
                    <div class="payment-method-item-content">
                        <div class="identifying-info">
                            <div class="label">Add New</div>
                            <mat-icon>add_circle_outline</mat-icon>
                        </div>
                    </div>
                </button>
            </li>
        </ol>
    `,
    styleUrls: ['./payment-method-selection-list.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PaymentMethodSelectionListComponent
{
    constructor
    (
        // Dependencies

        private readonly store: Store<State>,
    )
    {}


    // Configuration

    @Input() protected set paymentMethods(value: PaymentMethod[]) { this.paymentMethods$.next(value); }
    private readonly paymentMethods$ = new BehaviorSubject<PaymentMethod[] | undefined>(undefined);

    @Input() protected selectedPaymentMethod: string | null | undefined;
    @Input() protected allowNoneOption = false;
    @Input() protected showSelection = false;
    @Input() protected allowCreation = true;
    @Input() protected allowDeselection = true;


    // Payment methods

    protected readonly methods$ = combineLatest([this.paymentMethods$, this.store.select(selectPaymentMethods)]).pipe(
        map(([passedPaymentMethods, servicePaymentMethods]) => passedPaymentMethods || servicePaymentMethods)
    );


    // Events

    @Output() protected readonly selected: EventEmitter<string | null> = new EventEmitter<string | null>();
    @Output() protected readonly newMethodRequested: EventEmitter<void> = new EventEmitter<void>();


    // Actions

    protected selectMethod(paymentMethod: string | null)
    {
        if (paymentMethod !== this.selectedPaymentMethod)
        {
            this.selectedPaymentMethod = paymentMethod;
            this.selected.emit(paymentMethod);
        }
        else if (this.allowDeselection)
        {
            this.selectedPaymentMethod = null;
            this.selected.emit(paymentMethod);
        }
    }
}
